import { createReducer } from '@reduxjs/toolkit';
import type { RecorderPoint, RecorderStatus } from '../protocol.js';
import {
  type GpsRecorderConnection,
  type GpsRecorderFailure,
  gpsRecorderAddPoints,
  gpsRecorderSetConnection,
  gpsRecorderSetError,
  gpsRecorderSetPending,
  gpsRecorderSetStatus,
  gpsRecorderStop,
  gpsRecorderTrackCleared,
} from './actions.js';

export interface GpsRecorderState {
  status: RecorderStatus | null;
  /** The recorder's points as far as they are known here, ordered by `seq`. */
  points: RecorderPoint[];
  /**
   * Highest `seq` held, i.e. the `/track?since=` cursor.
   *
   * Deliberately not persisted: `statePersistingMiddleware` re-serializes the
   * whole persisted subset on every action, so a persisted cursor would cost a
   * full `JSON.stringify` per incoming fix. A cold start has no points anyway
   * and refetches the track from the recorder, which owns it.
   */
  cursor: number;
  /**
   * Whether a transport command the user gave is still in flight, so the
   * controls can wait for it without the background poll's own connecting
   * phases disabling them.
   */
  pending: boolean;
  /**
   * The recorder's `generation` as of the last status seen — how many times its
   * track has been thrown away. A change means the points held here are gone.
   * Null before any status has been read.
   */
  generation: number | null;
  connection: GpsRecorderConnection;
  error: GpsRecorderFailure | null;
}

export const gpsRecorderInitialState: GpsRecorderState = {
  status: null,
  points: [],
  cursor: 0,
  pending: false,
  generation: null,
  connection: 'idle',
  error: null,
};

/**
 * Merges a batch into a `seq`-ordered list, dropping seqs already held.
 *
 * Catch-up and the stream overlap by design — a resync refetches from the
 * cursor while the attached stream keeps delivering the same fixes live — so
 * batches arrive duplicated and, briefly, out of order.
 */
export function mergePoints(
  current: RecorderPoint[],
  incoming: RecorderPoint[],
): RecorderPoint[] {
  if (incoming.length === 0) {
    return current;
  }

  const maxSeq = current.length === 0 ? -Infinity : current.at(-1)!.seq;

  // The hot path: the stream feeding fixes onto the end of the track. Only the
  // batch needs ordering — re-sorting the whole track per fix would be
  // quadratic over a long recording.
  if (incoming.every((point) => point.seq > maxSeq)) {
    return [...current, ...[...incoming].sort((a, b) => a.seq - b.seq)];
  }

  const bySeq = new Map(current.map((point) => [point.seq, point]));

  for (const point of incoming) {
    if (!bySeq.has(point.seq)) {
      bySeq.set(point.seq, point);
    }
  }

  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export const gpsRecorderReducer = createReducer(
  gpsRecorderInitialState,
  (builder) =>
    builder
      // The recorder's own track is gone, so the copy of it goes too. Applied
      // only once the delete has been acknowledged, never optimistically.
      .addCase(gpsRecorderTrackCleared, (state) => ({
        ...state,
        points: [],
        cursor: 0,
      }))
      .addCase(gpsRecorderSetStatus, (state, { payload }) => ({
        ...state,
        status: payload,
        generation: payload?.generation ?? state.generation,
      }))
      .addCase(gpsRecorderAddPoints, (state, { payload }) => {
        const points = mergePoints(state.points, payload);

        return {
          ...state,
          points,
          cursor: points.length === 0 ? 0 : points.at(-1)!.seq,
        };
      })
      .addCase(gpsRecorderSetPending, (state, { payload }) => ({
        ...state,
        pending: payload,
      }))
      .addCase(gpsRecorderSetConnection, (state, { payload }) => ({
        ...state,
        connection: payload,
      }))
      .addCase(gpsRecorderSetError, (state, { payload }) => ({
        ...state,
        error: payload,
      }))
      .addCase(gpsRecorderStop, (state) => ({
        ...state,
        error: null,
      })),
);
